import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, Trash2, Users, Loader2, CheckCircle, Clock, Pencil, ShieldCheck, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function AdminVolunteers() {
  const [volunteers, setVolunteers]       = useState([]);
  const [volStats, setVolStats]           = useState({});
  const [loading, setLoading]             = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", wheelchair_license: false, is_test: false });
  const [adding, setAdding]               = useState(false);
  const [deleting, setDeleting]           = useState(null);
  const [editVol, setEditVol]             = useState(null); // { id, name, email, phone }
  const [editSaving, setEditSaving]       = useState(false);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.getVolunteers().then((r) => setVolunteers(r.data.volunteers)),
      api.getVolunteerStats().then((r) => {
        const map = {};
        (r.data.volunteers || []).forEach((v) => { map[v.name] = v; });
        setVolStats(map);
      }),
    ])
      .catch(() => toast.error("Failed to load volunteers"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await api.addVolunteer(form);
      setVolunteers((prev) => [...prev, data.volunteer]);
      setForm({ name: "", email: "", phone: "", wheelchair_license: false, is_test: false });
      toast.success(`${form.name} added as a volunteer`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add volunteer");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (vol) => {
    if (!window.confirm(`Remove ${vol.name} from volunteers?`)) return;
    setDeleting(vol.id);
    try {
      await api.deleteVolunteer(vol.id);
      setVolunteers((prev) => prev.filter((v) => v.id !== vol.id));
      toast.success(`${vol.name} removed from volunteers`);
    } catch {
      toast.error("Failed to remove volunteer");
    } finally {
      setDeleting(null);
    }
  };

  const openEdit = (vol) => setEditVol({ id: vol.id, name: vol.name, email: vol.email, phone: vol.phone || "", wheelchair_license: vol.wheelchair_license || false, is_test: vol.is_test || false });

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      const { data } = await api.updateVolunteer(editVol.id, { name: editVol.name, email: editVol.email, phone: editVol.phone, wheelchair_license: editVol.wheelchair_license, is_test: editVol.is_test });
      setVolunteers((prev) => prev.map((v) => v.id === editVol.id ? data.volunteer : v));
      toast.success("Volunteer updated");
      setEditVol(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update volunteer");
    } finally {
      setEditSaving(false);
    }
  };

  const formatLastActive = (isoStr) => {
    if (!isoStr) return "Never";
    try {
      return new Date(isoStr).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return "—";
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Volunteers</h1>
          <p className="text-slate-500 mt-1">Manage volunteers and view their booking performance</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => api.exportVolunteersCSV().then(r => { const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "volunteers.csv"; a.click(); URL.revokeObjectURL(url); })} 
          className="gap-2 border-slate-200 text-slate-600 hover:bg-slate-50" 
          data-testid="export-volunteers-btn"
        >
          <Download className="w-4 h-4" /> Export
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add Volunteer Form */}
        <div className="bg-white rounded-2xl border border-green-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <UserPlus className="w-5 h-5 text-green-700" />
            <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Add Volunteer</h2>
          </div>
          <form onSubmit={handleAdd} className="space-y-4" data-testid="add-volunteer-form">
            <div>
              <Label className="text-slate-600 font-medium mb-2 block" htmlFor="vol-name">Full Name</Label>
              <Input id="vol-name" data-testid="volunteer-name-field" type="text" placeholder="e.g. John Murphy"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                className="h-11 bg-slate-50 border-slate-200 focus:bg-white focus:border-green-600" />
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block" htmlFor="vol-email">Email Address</Label>
              <Input id="vol-email" data-testid="volunteer-email-field" type="email" placeholder="e.g. john@example.com"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
                className="h-11 bg-slate-50 border-slate-200 focus:bg-white focus:border-green-600" />
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block" htmlFor="vol-phone">Phone Number</Label>
              <Input id="vol-phone" data-testid="volunteer-phone-field" type="tel" placeholder="e.g. +353 87 123 4567"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required
                className="h-11 bg-slate-50 border-slate-200 focus:bg-white focus:border-green-600" />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <label className="flex items-center gap-3 cursor-pointer" htmlFor="vol-wc-license">
                <input id="vol-wc-license" data-testid="volunteer-wheelchair-license-field" type="checkbox"
                  checked={form.wheelchair_license}
                  onChange={(e) => setForm({ ...form, wheelchair_license: e.target.checked })}
                  className="w-4 h-4 accent-blue-600" />
                <span className="text-sm text-slate-700 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">Wheelchair Bike Licence</span>
                </span>
              </label>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <label className="flex items-center gap-3 cursor-pointer" htmlFor="vol-is-test">
                <input id="vol-is-test" data-testid="volunteer-is-test-field" type="checkbox"
                  checked={form.is_test}
                  onChange={(e) => setForm({ ...form, is_test: e.target.checked })}
                  className="w-4 h-4 accent-amber-600" />
                <span className="text-sm text-slate-700 font-medium">Test Volunteer (maintenance mode only)</span>
              </label>
            </div>
            <Button type="submit" disabled={adding} data-testid="add-volunteer-btn"
              className="w-full rounded-full bg-[#15803d] hover:bg-green-800 text-white">
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              {adding ? "Adding..." : "Add Volunteer"}
            </Button>
          </form>
        </div>

        {/* Volunteers List with Performance */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-100">
            <Users className="w-5 h-5 text-green-700" />
            <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Current Volunteers ({volunteers.length})
            </h2>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : volunteers.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>No volunteers yet. Add your first volunteer above.</p>
            </div>
          ) : (
            <div>
              {/* Performance header row */}
              <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 px-6 py-2 bg-slate-50 text-slate-500 text-xs uppercase tracking-wide font-semibold border-b border-slate-100">
                <span>Volunteer</span>
                <span className="w-20 text-center">Bookings</span>
                <span className="w-20 text-center">Hours</span>
                <span className="w-28 text-center">Last Active</span>
                <span className="w-20" />
              </div>
              <div data-testid="volunteers-list">
                {volunteers.map((vol, i) => {
                  const perf = volStats[vol.name];
                  return (
                    <div
                      key={vol.id}
                      data-testid={`volunteer-row-${vol.id}`}
                      className={`grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-2 px-6 py-4 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} border-b border-slate-100 last:border-0`}
                    >
                      <div>
                        <p className="font-semibold text-slate-800">{vol.name}</p>
                        <p className="text-slate-400 text-xs">{vol.email}</p>
                        {vol.phone && <p className="text-slate-400 text-xs">{vol.phone}</p>}
                        {vol.wheelchair_license && (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 mt-1" data-testid={`vol-wc-badge-${vol.id}`}>
                            <ShieldCheck className="w-3 h-3" /> Wheelchair Licence
                          </span>
                        )}
                        {vol.is_test && (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mt-1 ml-1" data-testid={`vol-test-badge-${vol.id}`}>
                            Test
                          </span>
                        )}
                      </div>
                      {/* Confirmed bookings */}
                      <div className="w-20 flex justify-center">
                        {perf?.confirmed > 0 ? (
                          <span className="flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-semibold" data-testid={`vol-confirmed-${vol.id}`}>
                            <CheckCircle className="w-3 h-3" /> {perf.confirmed}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </div>
                      {/* Total hours */}
                      <div className="w-20 flex justify-center">
                        {perf?.total_hours > 0 ? (
                          <span className="flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-xs font-semibold" data-testid={`vol-hours-${vol.id}`}>
                            <Clock className="w-3 h-3" /> {perf.total_hours}h
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </div>
                      {/* Last active */}
                      <div className="w-28 flex justify-center">
                        <span className={`text-xs font-medium ${perf?.last_booking ? "text-slate-600" : "text-slate-300"}`}
                          data-testid={`vol-last-${vol.id}`}>
                          {formatLastActive(perf?.last_booking)}
                        </span>
                      </div>
                      {/* Edit + Delete */}
                      <div className="w-20 flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => openEdit(vol)}
                          data-testid={`edit-volunteer-${vol.id}`} title="Edit volunteer"
                          className="border-green-200 text-green-700 hover:bg-green-50 h-8 w-8 p-0">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(vol)} disabled={deleting === vol.id}
                          data-testid={`delete-volunteer-${vol.id}`} className="border-red-200 text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                          {deleting === vol.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Volunteer Dialog */}
      <Dialog open={!!editVol} onOpenChange={(o) => !o && setEditVol(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="edit-volunteer-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Edit Volunteer</DialogTitle>
          </DialogHeader>
          {editVol && (
            <div className="space-y-3 py-1">
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Full Name</Label>
                <Input data-testid="edit-vol-name-input" placeholder="Full name"
                  value={editVol.name} onChange={(e) => setEditVol({ ...editVol, name: e.target.value })}
                  className="h-11 bg-slate-50 border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Email Address</Label>
                <Input data-testid="edit-vol-email-input" type="email" placeholder="Email address"
                  value={editVol.email} onChange={(e) => setEditVol({ ...editVol, email: e.target.value })}
                  className="h-11 bg-slate-50 border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Phone Number</Label>
                <Input data-testid="edit-vol-phone-input" type="tel" placeholder="+353 87 123 4567"
                  value={editVol.phone} onChange={(e) => setEditVol({ ...editVol, phone: e.target.value })}
                  className="h-11 bg-slate-50 border-slate-200" />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <label className="flex items-center gap-3 cursor-pointer" htmlFor="edit-vol-wc-license">
                  <input id="edit-vol-wc-license" data-testid="edit-vol-wheelchair-license" type="checkbox"
                    checked={editVol.wheelchair_license || false}
                    onChange={(e) => setEditVol({ ...editVol, wheelchair_license: e.target.checked })}
                    className="w-4 h-4 accent-blue-600" />
                  <span className="text-sm text-slate-700 flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">Wheelchair Bike Licence</span>
                  </span>
                </label>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <label className="flex items-center gap-3 cursor-pointer" htmlFor="edit-vol-is-test">
                  <input id="edit-vol-is-test" data-testid="edit-vol-is-test" type="checkbox"
                    checked={editVol.is_test || false}
                    onChange={(e) => setEditVol({ ...editVol, is_test: e.target.checked })}
                    className="w-4 h-4 accent-amber-600" />
                  <span className="text-sm text-slate-700 font-medium">Test Volunteer (maintenance mode only)</span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVol(null)} disabled={editSaving}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving} data-testid="save-volunteer-btn"
              className="bg-[#15803d] hover:bg-green-800 text-white gap-2">
              {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {editSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
